import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledLink from "@tSurya/theme/components/StyledLink";
import { TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";

const IG = (WIDGETS as any).imageGrid || {};
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const W = (WIDGETS as any).flexibleContent || {};

const enum fields {
    layout = "layout",
    backgroundColor = "backgroundColor",
    image1 = "image1",
    image1Alt = "image1Alt",
    image1Link = "image1Link",
    image2 = "image2",
    image2Alt = "image2Alt",
    image2Link = "image2Link",
    preTitle = "preTitle",
    title = "title",
    description = "description",
    ctaLabel = "ctaLabel",
    ctaLink = "ctaLink",
    ctaStyle = "ctaStyle",
    ctaRequiresAuthentication = "ctaRequiresAuthentication",
    mobileOrder = "mobileOrder",
    textAlign = "textAlign",
    mobileTextAlign = "mobileTextAlign",
    preTitleFontSize = "preTitleFontSize",
    mobilePreTitleFontSize = "mobilePreTitleFontSize",
    titleFontSize = "titleFontSize",
    mobileTitleFontSize = "mobileTitleFontSize",
    paddingVertical = "paddingVertical",
    paddingHorizontal = "paddingHorizontal",
    mobilePaddingVertical = "mobilePaddingVertical",
    mobilePaddingHorizontal = "mobilePaddingHorizontal",
    imageAspectRatio = "imageAspectRatio",
    fullWidth = "fullWidth",
    textColor = "textColor",
    ctaColor = "ctaColor",
    descriptionFontWeight = "descriptionFontWeight",
    mobileDescriptionFontWeight = "mobileDescriptionFontWeight",
    preTitleFontWeight = "preTitleFontWeight",
    mobilePreTitleFontWeight = "mobilePreTitleFontWeight",
    titleFontWeight = "titleFontWeight",
    mobileTitleFontWeight = "mobileTitleFontWeight",
    gap = "gap",
    textContentMaxWidth = "textContentMaxWidth",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.layout]: string;
        [fields.backgroundColor]: string;
        [fields.image1]: string;
        [fields.image1Alt]: string;
        [fields.image1Link]: LinkFieldValue;
        [fields.image2]: string;
        [fields.image2Alt]: string;
        [fields.image2Link]: LinkFieldValue;
        [fields.preTitle]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.ctaLabel]: string;
        [fields.ctaLink]: LinkFieldValue;
        [fields.ctaStyle]?: string;
        [fields.ctaRequiresAuthentication]?: boolean;
        [fields.mobileOrder]: string;
        [fields.textAlign]: string;
        [fields.mobileTextAlign]: string;
        [fields.preTitleFontSize]: string;
        [fields.mobilePreTitleFontSize]: string;
        [fields.titleFontSize]: string;
        [fields.mobileTitleFontSize]: string;
        [fields.paddingVertical]: string;
        [fields.paddingHorizontal]: string;
        [fields.mobilePaddingVertical]: string;
        [fields.mobilePaddingHorizontal]: string;
        [fields.imageAspectRatio]: string;
        [fields.fullWidth]: boolean;
        [fields.textColor]: string;
        [fields.ctaColor]: string;
        [fields.descriptionFontWeight]: string;
        [fields.mobileDescriptionFontWeight]: string;
        [fields.preTitleFontWeight]: string;
        [fields.mobilePreTitleFontWeight]: string;
        [fields.titleFontWeight]: string;
        [fields.mobileTitleFontWeight]: string;
        [fields.gap]: string;
        [fields.textContentMaxWidth]?: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: state.context.session.isAuthenticated,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SurFlexibleContentStyles {
    outerWrapper?: InjectableCss;
    container?: InjectableCss;
    imagesArea?: InjectableCss;
    imageWrapper?: InjectableCss;
    imageElement?: InjectableCss;
    singleImageWrapper?: InjectableCss;
    singleImageTextArea?: InjectableCss;
    textArea?: InjectableCss;
    textContent?: InjectableCss;
    titlesWrapper?: InjectableCss;
    preTitle?: TypographyProps;
    title?: TypographyProps;
    descriptionText?: InjectableCss;
    ctaButton?: InjectableCss;
}

export const surFlexibleContentStyles: SurFlexibleContentStyles = {
    outerWrapper: {
        css: css`
            width: 100%;
        `,
    },
    container: {
        css: css`
            width: 100%;
            max-width: 1440px;
            margin: 0 auto;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: ${W.gap || "40px"};
            padding: ${W.padding || "100px 40px"};

            /* Mobile reordering: when layout puts images first in DOM but mobile wants text first */
            &.mobile-swap {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            > *:first-child {
                                order: 2;
                            }
                            > *:last-child {
                                order: 1;
                            }
                        `,
                        css`
                            > *:first-child {
                                order: 2;
                            }
                            > *:last-child {
                                order: 1;
                            }
                        `,
                        css`
                            > *:first-child {
                                order: 2;
                            }
                            > *:last-child {
                                order: 1;
                            }
                        `,
                        null,
                        null,
                    ])}
            }

            /* Mobile text-between: image1, text, image2 */
            &.mobile-text-between {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            > .flex-images-area {
                                display: contents;
                                > *:first-child {
                                    order: 1;
                                    width: 100%;
                                    height: ${W.mobileImageHeight || "300px"};
                                }
                                > *:last-child {
                                    order: 3;
                                    width: 100%;
                                    height: ${W.mobileImageHeight || "300px"};
                                }
                            }
                            > *:not(.flex-images-area) {
                                order: 2;
                            }
                        `,
                        css`
                            > .flex-images-area {
                                display: contents;
                                > *:first-child {
                                    order: 1;
                                    width: 100%;
                                    height: ${W.mobileImageHeight || "300px"};
                                }
                                > *:last-child {
                                    order: 3;
                                    width: 100%;
                                    height: ${W.mobileImageHeight || "300px"};
                                }
                            }
                            > *:not(.flex-images-area) {
                                order: 2;
                            }
                        `,
                        css`
                            > .flex-images-area {
                                display: contents;
                                > *:first-child {
                                    order: 1;
                                    width: 100%;
                                    height: ${W.mobileImageHeight || "300px"};
                                }
                                > *:last-child {
                                    order: 3;
                                    width: 100%;
                                    height: ${W.mobileImageHeight || "300px"};
                                }
                            }
                            > *:not(.flex-images-area) {
                                order: 2;
                            }
                        `,
                        null,
                        null,
                    ])}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        padding: ${W.mobilePadding || "32px 16px"};
                        gap: ${W.mobileGap || "32px"};
                        align-items: flex-start;
                    `,
                    css`
                        flex-direction: column;
                        padding: ${W.mobilePadding || "32px 16px"};
                        gap: ${W.mobileGap || "32px"};
                        align-items: flex-start;
                    `,
                    css`
                        flex-direction: column;
                        padding: ${W.mobilePadding || "32px 16px"};
                        gap: ${W.mobileGap || "32px"};
                        align-items: flex-start;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    imagesArea: {
        css: css`
            display: flex;
            flex: 1;
            min-width: 0;
            gap: ${W.imagesGap || "20px"};
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        width: 100%;
                        gap: ${W.mobileImagesGap || "12px"};
                    `,
                    css`
                        flex-direction: column;
                        width: 100%;
                        gap: ${W.mobileImagesGap || "12px"};
                    `,
                    css`
                        flex-direction: column;
                        width: 100%;
                        gap: ${W.mobileImagesGap || "12px"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    imageWrapper: {
        css: css`
            flex: 1;
            min-width: 0;
            height: ${W.imageHeight || "400px"};
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        height: ${W.mobileImageHeight || "300px"};
                    `,
                    css`
                        width: 100%;
                        height: ${W.mobileImageHeight || "300px"};
                    `,
                    css`
                        width: 100%;
                        height: ${W.mobileImageHeight || "300px"};
                    `,
                    null,
                    null,
                ])}

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        `,
    },
    imageElement: {
        css: css`
            width: 100%;
            height: 100%;
            object-fit: cover;
        `,
    },
    singleImageWrapper: {
        css: css`
            flex-basis: ${IG.imageFlexBasis || "60%"};
            max-width: ${IG.imageMaxWidth || "700px"};
            overflow: hidden;

            img {
                width: 100%;
                height: auto;
                object-fit: cover;
                aspect-ratio: ${IG.image?.aspectRatio || "1.25"};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: none;
                        img {
                            aspect-ratio: ${IG.image?.mobileAspectRatio || "358/240"};
                        }
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: none;
                        img {
                            aspect-ratio: ${IG.image?.mobileAspectRatio || "358/240"};
                        }
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: none;
                        img {
                            aspect-ratio: ${IG.image?.mobileAspectRatio || "358/240"};
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    singleImageTextArea: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${W.contentToCtaGap || "32px"};
            justify-content: center;
            align-items: flex-start;
            flex: 1;
            max-width: ${IG.contentMaxWidth || "550px"};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: none;
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    css`
                        width: 100%;
                        max-width: none;
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    css`
                        width: 100%;
                        max-width: none;
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    textArea: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${W.contentToCtaGap || "32px"};
            justify-content: center;
            align-items: flex-start;
            flex-shrink: 0;
            width: ${W.textWidth || "400px"};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    css`
                        width: 100%;
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    css`
                        width: 100%;
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    textContent: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${W.textBlockGap || "16px"};
            width: 100%;
            max-width: 100%;
            margin: 0;
            word-break: break-word;
        `,
    },
    titlesWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${W.titlesGap || "4px"};
            width: 100%;
        `,
    },
    preTitle: {
        css: css`
            font-family: ${W.preTitle?.fontFamily || TYPOGRAPHY.fontFamily.title};
            font-style: ${W.preTitle?.fontStyle || "italic"};
            font-size: ${W.preTitle?.fontSize || "24px"};
            font-weight: ${W.preTitle?.fontWeight || "400"};
            line-height: ${W.preTitle?.lineHeight || "1.5"};
            letter-spacing: ${W.preTitle?.letterSpacing || "0.8px"};
            color: ${W.preTitle?.color || getColor("text.main")};
            margin: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${W.preTitle?.mobileFontSize || W.preTitle?.fontSize || "20px"};
                    `,
                    css`
                        font-size: ${W.preTitle?.mobileFontSize || W.preTitle?.fontSize || "20px"};
                    `,
                    css`
                        font-size: ${W.preTitle?.mobileFontSize || W.preTitle?.fontSize || "20px"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    title: {
        variant: "h2",
        css: css`
            font-family: ${W.title?.fontFamily || TYPOGRAPHY.fontFamily.title};
            font-size: ${W.title?.fontSize || "40px"};
            font-weight: ${W.title?.fontWeight || "500"};
            line-height: ${W.title?.lineHeight || "normal"};
            color: ${getColor("text.main")};
            margin: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${W.title?.mobileFontSize || W.title?.fontSize || "32px"};
                    `,
                    css`
                        font-size: ${W.title?.mobileFontSize || W.title?.fontSize || "32px"};
                    `,
                    css`
                        font-size: ${W.title?.mobileFontSize || W.title?.fontSize || "32px"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    descriptionText: {
        css: css`
            font-family: ${W.description?.fontFamily || "inherit"};
            font-size: ${W.description?.fontSize || "15px"};
            font-weight: ${W.description?.fontWeight || "400"};
            line-height: ${W.description?.lineHeight || "1.5"};
            letter-spacing: ${W.description?.letterSpacing || "0.08px"};
            color: ${getColor("text.main")};
            margin: 0;
            p {
                margin: 0;
            }
        `,
    },
    ctaButton: {
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: ${W.cta?.height || "44px"};
            padding: ${W.cta?.padding || "10px 24px"};
            background-color: ${W.cta?.backgroundColor || getColor("common.background")};
            border: 1px solid ${W.cta?.borderColor || getColor("text.main")};
            color: ${W.cta?.color || getColor("text.main")};
            font-family: ${W.cta?.fontFamily || "inherit"};
            font-size: ${W.cta?.fontSize || "15px"};
            font-weight: ${W.cta?.fontWeight || "500"};
            line-height: ${W.cta?.lineHeight || "1.5"};
            letter-spacing: ${W.cta?.letterSpacing || "0.08px"};
            text-decoration: none;
            cursor: pointer;
            white-space: nowrap;
            box-shadow: 0px 1px 2px 0px rgba(10, 13, 18, 0.05);
            transition: all 0.3s ease;

            &:hover {
                background-color: ${getColor("text.main")};
                border-color: ${getColor("text.main")};
                color: ${getColor("common.background")};
                * {
                    color: ${getColor("common.background")} !important;
                }
            }

            * {
                color: inherit !important;
                text-decoration: none;
            }
        `,
    },
};

const styles = surFlexibleContentStyles;

const ImageBlock: React.FC<{ src: string; alt: string; link?: string }> = ({ src, alt, link }) => {
    const content = (
        <StyledWrapper {...styles.imageWrapper}>
            <LazyImage src={src} altText={alt || ""} imgProps={{ style: { width: "100%", height: "100%", objectFit: "cover" } }} />
        </StyledWrapper>
    );

    if (link) {
        return (
            <Link href={link} style={{ display: "contents" }}>
                {content}
            </Link>
        );
    }

    return content;
};

const SurFlexibleContent: React.FC<Props> = ({ fields: f, isAuthenticated }) => {
    const { url: ctaUrl } = useGetLink(f.ctaLink);
    const { url: image1Url } = useGetLink(f.image1Link);
    const { url: image2Url } = useGetLink(f.image2Link);

    const layout = f.layout || "images-left";
    const mobileOrder = f.mobileOrder || "text-first";
    const bgColor = f.backgroundColor || "transparent";
    const hasImage1 = !!f.image1;
    const hasImage2 = !!f.image2;
    const hasPreTitle = !!f.preTitle;
    const hasDescription = !!f.description;
    const hasCta = !!f.ctaLabel && !!ctaUrl;
    const ctaStyle = f.ctaStyle || "button";
    const isSplit = layout === "split" && hasImage1 && hasImage2;
    const imageAspectRatio = f.imageAspectRatio || "";

    // Description font-weight override from CMS
    const descFontWeightOverrideCss =
        f.descriptionFontWeight || f.mobileDescriptionFontWeight
            ? css`
                  ${f.descriptionFontWeight ? `font-weight: ${f.descriptionFontWeight};` : ""}
                  ${f.mobileDescriptionFontWeight
                      ? ({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    font-weight: ${f.mobileDescriptionFontWeight};
                                `,
                                css`
                                    font-weight: ${f.mobileDescriptionFontWeight};
                                `,
                                css`
                                    font-weight: ${f.mobileDescriptionFontWeight};
                                `,
                                null,
                                null,
                            ])
                      : ""}
              `
            : undefined;

    // Determine if we need to swap order on mobile.
    // DOM order is set for desktop correctness. On mobile (column),
    // if the DOM-first element should appear second, add "mobile-swap".
    const desktopImagesFirst = layout !== "images-right";
    const mobileTextFirst = mobileOrder === "text-first";
    const isTextBetween = mobileOrder === "text-between" && hasImage1 && hasImage2;
    const needsMobileSwap = !isTextBetween && desktopImagesFirst === mobileTextFirst;
    // images-left + text-first → DOM: [images][text], mobile wants [text][images] → swap
    // images-left + images-first → DOM: [images][text], mobile wants [images][text] → no swap
    // images-right + text-first → DOM: [text][images], mobile wants [text][images] → no swap
    // images-right + images-first → DOM: [text][images], mobile wants [images][text] → swap
    // text-between → handled by separate CSS class, no swap needed

    const singleImage = hasImage1 && !hasImage2;
    const isFullWidth = !!f.fullWidth && singleImage;

    const desktopAlign = f.textAlign || "left";
    const mobAlign = f.mobileTextAlign || "left";
    const alignItemsMap: Record<string, string> = { left: "flex-start", center: "center", right: "flex-end" };
    const textAlignCss = css`
        text-align: ${desktopAlign};
        align-items: ${alignItemsMap[desktopAlign] || "flex-start"};
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    text-align: ${mobAlign};
                    align-items: ${alignItemsMap[mobAlign] || "flex-start"};
                `,
                css`
                    text-align: ${mobAlign};
                    align-items: ${alignItemsMap[mobAlign] || "flex-start"};
                `,
                css`
                    text-align: ${mobAlign};
                    align-items: ${alignItemsMap[mobAlign] || "flex-start"};
                `,
                null,
                null,
            ])}
    `;
    let textAreaBaseCss = (singleImage ? styles.singleImageTextArea : styles.textArea)?.css;

    // Full-width mode: text area gets 50% width with internal padding; container has no padding
    if (isFullWidth) {
        textAreaBaseCss = css`
            display: flex;
            flex-direction: column;
            gap: ${W.contentToCtaGap || "32px"};
            justify-content: center;
            align-items: flex-start;
            flex: 0 0 50%;
            max-width: none;
            box-sizing: border-box;
            padding: ${f.paddingVertical || "60px"} ${f.paddingHorizontal || "40px"};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        width: 100%;
                        padding: ${f.mobilePaddingVertical || "32px"} ${f.mobilePaddingHorizontal || "16px"};
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    css`
                        flex-basis: 100%;
                        width: 100%;
                        padding: ${f.mobilePaddingVertical || "32px"} ${f.mobilePaddingHorizontal || "16px"};
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    css`
                        flex-basis: 100%;
                        width: 100%;
                        padding: ${f.mobilePaddingVertical || "32px"} ${f.mobilePaddingHorizontal || "16px"};
                        gap: ${W.mobileContentToCtaGap || "20px"};
                    `,
                    null,
                    null,
                ])}
        `;
    }

    // Container padding overrides from CMS
    const hasPaddingOverride = !!f.paddingVertical || !!f.paddingHorizontal;
    const hasMobilePaddingOverride = !!f.mobilePaddingVertical || !!f.mobilePaddingHorizontal;
    const containerOverrideCss =
        hasPaddingOverride || hasMobilePaddingOverride
            ? css`
                  ${hasPaddingOverride ? `padding: ${f.paddingVertical || "100px"} ${f.paddingHorizontal || "40px"};` : ""}
                  ${hasMobilePaddingOverride
                      ? ({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding: ${f.mobilePaddingVertical || "32px"} ${f.mobilePaddingHorizontal || "16px"};
                                `,
                                css`
                                    padding: ${f.mobilePaddingVertical || "32px"} ${f.mobilePaddingHorizontal || "16px"};
                                `,
                                css`
                                    padding: ${f.mobilePaddingVertical || "32px"} ${f.mobilePaddingHorizontal || "16px"};
                                `,
                                null,
                                null,
                            ])
                      : ""}
              `
            : undefined;

    // Font size overrides from CMS for preTitle and title
    const preTitleOverrideCss =
        f.preTitleFontSize || f.mobilePreTitleFontSize || f.preTitleFontWeight || f.mobilePreTitleFontWeight
            ? css`
                  ${f.preTitleFontSize ? `font-size: ${f.preTitleFontSize};` : ""}
                  ${f.preTitleFontWeight ? `font-weight: ${f.preTitleFontWeight};` : ""}
        ${f.mobilePreTitleFontSize || f.mobilePreTitleFontWeight
                      ? ({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    ${f.mobilePreTitleFontSize ? `font-size: ${f.mobilePreTitleFontSize};` : ""} ${f.mobilePreTitleFontWeight
                                        ? `font-weight: ${f.mobilePreTitleFontWeight};`
                                        : ""}
                                `,
                                css`
                                    ${f.mobilePreTitleFontSize ? `font-size: ${f.mobilePreTitleFontSize};` : ""} ${f.mobilePreTitleFontWeight
                                        ? `font-weight: ${f.mobilePreTitleFontWeight};`
                                        : ""}
                                `,
                                css`
                                    ${f.mobilePreTitleFontSize ? `font-size: ${f.mobilePreTitleFontSize};` : ""} ${f.mobilePreTitleFontWeight
                                        ? `font-weight: ${f.mobilePreTitleFontWeight};`
                                        : ""}
                                `,
                                null,
                                null,
                            ])
                      : ""}
              `
            : undefined;
    const titleOverrideCss =
        f.titleFontSize || f.mobileTitleFontSize || f.titleFontWeight || f.mobileTitleFontWeight
            ? css`
                  ${f.titleFontSize ? `font-size: ${f.titleFontSize};` : ""}
                  ${f.titleFontWeight ? `font-weight: ${f.titleFontWeight};` : ""}
        ${f.mobileTitleFontSize || f.mobileTitleFontWeight
                      ? ({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    ${f.mobileTitleFontSize ? `font-size: ${f.mobileTitleFontSize};` : ""} ${f.mobileTitleFontWeight
                                        ? `font-weight: ${f.mobileTitleFontWeight};`
                                        : ""}
                                `,
                                css`
                                    ${f.mobileTitleFontSize ? `font-size: ${f.mobileTitleFontSize};` : ""} ${f.mobileTitleFontWeight
                                        ? `font-weight: ${f.mobileTitleFontWeight};`
                                        : ""}
                                `,
                                css`
                                    ${f.mobileTitleFontSize ? `font-size: ${f.mobileTitleFontSize};` : ""} ${f.mobileTitleFontWeight
                                        ? `font-weight: ${f.mobileTitleFontWeight};`
                                        : ""}
                                `,
                                null,
                                null,
                            ])
                      : ""}
              `
            : undefined;

    const gapOverrideCss = f.gap
        ? css`
              gap: ${f.gap};
          `
        : undefined;

    const textContentMaxWidthCss = f.textContentMaxWidth
        ? css`
              max-width: ${f.textContentMaxWidth};
          `
        : undefined;

    const textContentProps = textContentMaxWidthCss
        ? {
              ...styles.textContent,
              css: css`
                  ${styles.textContent?.css} ${textContentMaxWidthCss}
              `,
          }
        : styles.textContent;

    let containerCss =
        containerOverrideCss || gapOverrideCss
            ? css`
                  ${styles.container?.css} ${containerOverrideCss} ${gapOverrideCss}
              `
            : styles.container?.css;

    // Full-width mode: container has no max-width, no padding, no gap
    if (isFullWidth) {
        containerCss = css`
            width: 100%;
            display: flex;
            flex-direction: row;
            align-items: stretch;
            max-width: none;
            padding: 0;
            gap: 0;

            &.mobile-swap {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            > *:first-child {
                                order: 2;
                            }
                            > *:last-child {
                                order: 1;
                            }
                        `,
                        css`
                            > *:first-child {
                                order: 2;
                            }
                            > *:last-child {
                                order: 1;
                            }
                        `,
                        css`
                            > *:first-child {
                                order: 2;
                            }
                            > *:last-child {
                                order: 1;
                            }
                        `,
                        null,
                        null,
                    ])}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                ])}
        `;
    }

    const preTitleProps = preTitleOverrideCss
        ? {
              ...styles.preTitle,
              css: css`
                  ${styles.preTitle?.css} ${preTitleOverrideCss}
              `,
          }
        : styles.preTitle;
    const titleProps = titleOverrideCss
        ? {
              ...styles.title,
              css: css`
                  ${styles.title?.css} ${titleOverrideCss}
              `,
          }
        : styles.title;

    // Text color override from CMS
    const textColor = f.textColor || "";
    const finalPreTitleProps = textColor
        ? {
              ...preTitleProps,
              css: css`
                  ${preTitleProps?.css} color: ${textColor};
              `,
          }
        : preTitleProps;
    const finalTitleProps = textColor
        ? {
              ...titleProps,
              css: css`
                  ${titleProps?.css} color: ${textColor};
              `,
          }
        : titleProps;
    const baseDescriptionProps = descFontWeightOverrideCss
        ? {
              ...styles.descriptionText,
              css: css`
                  ${styles.descriptionText?.css} ${descFontWeightOverrideCss}
              `,
          }
        : styles.descriptionText;
    const descriptionTextProps = textColor
        ? {
              ...baseDescriptionProps,
              css: css`
                  ${baseDescriptionProps?.css} color: ${textColor};
                  p {
                      color: ${textColor};
                  }
              `,
          }
        : baseDescriptionProps;
    const ctaColorVal = f.ctaColor || textColor || "";
    const ctaButtonProps = ctaColorVal
        ? {
              ...styles.ctaButton,
              css: css`
                  ${styles.ctaButton?.css} color: ${ctaColorVal};
                  border-color: ${ctaColorVal};
                  * {
                      color: ${ctaColorVal} !important;
                  }
              `,
          }
        : styles.ctaButton;

    const singleImageProps =
        imageAspectRatio && !isFullWidth
            ? {
                  ...styles.singleImageWrapper,
                  css: css`
                      ${styles.singleImageWrapper?.css}
                      img {
                          aspect-ratio: ${imageAspectRatio};
                      }
                  `,
              }
            : styles.singleImageWrapper;

    // Full-width image wrapper: 50% width, no aspect-ratio, fills container height
    const singleImageWrapperProps = isFullWidth
        ? {
              css: css`
                  flex: 0 0 50%;
                  max-width: none;
                  overflow: hidden;
                  height: 100%;
                  display: flex;

                  & * {
                      flex: 1;
                      min-height: 0;
                  }

                  img {
                      width: 100%;
                      height: 100%;
                      object-fit: cover;
                      display: block;
                  }
                  ${({ theme }: { theme: BaseTheme }) =>
                      breakpointMediaQueries(theme, [
                          css`
                              flex: none;
                              height: auto;
                              width: 100%;
                              display: block;
                              overflow: hidden;
                              line-height: 0;

                              & * {
                                  display: block;
                                  width: 100% !important;
                                  line-height: 0;
                              }

                              img {
                                  display: block;
                                  width: 100%;
                                  height: auto;
                              }
                          `,
                          css`
                              flex: none;
                              height: auto;
                              width: 100%;
                              display: block;
                              overflow: hidden;
                              line-height: 0;

                              & * {
                                  display: block;
                                  width: 100% !important;
                                  line-height: 0;
                              }

                              img {
                                  display: block;
                                  width: 100%;
                                  height: auto;
                              }
                          `,
                          css`
                              flex: none;
                              height: auto;
                              width: 100%;
                              display: block;
                              overflow: hidden;
                              line-height: 0;

                              & * {
                                  display: block;
                                  width: 100% !important;
                                  line-height: 0;
                              }

                              img {
                                  display: block;
                                  width: 100%;
                                  height: auto;
                              }
                          `,
                          null,
                          null,
                      ])}
              `,
          }
        : singleImageProps;

    const textAreaBlock = (
        <StyledWrapper
            css={css`
                ${textAreaBaseCss} ${textAlignCss}
            `}
        >
            <StyledWrapper {...textContentProps}>
                <StyledWrapper {...styles.titlesWrapper}>
                    {hasPreTitle && <Typography {...finalPreTitleProps}>{f.preTitle}</Typography>}
                    <Typography {...finalTitleProps} as="h2" role="heading">
                        {f.title}
                    </Typography>
                </StyledWrapper>
                {hasDescription && <StyledWrapper {...descriptionTextProps} dangerouslySetInnerHTML={{ __html: f.description }} />}
            </StyledWrapper>
            {hasCta &&
                (!f.ctaRequiresAuthentication || isAuthenticated) &&
                (ctaStyle === "link" ? (
                    <StyledLink href={ctaUrl} textColor={ctaColorVal || undefined}>
                        {f.ctaLabel}
                    </StyledLink>
                ) : (
                    <StyledWrapper {...ctaButtonProps}>
                        <Link href={ctaUrl}>{f.ctaLabel}</Link>
                    </StyledWrapper>
                ))}
        </StyledWrapper>
    );

    const imagesAreaBlock = hasImage1 ? (
        singleImage ? (
            image1Url ? (
                <Link href={image1Url} style={{ display: "contents", width: "100%" }}>
                    <StyledWrapper {...singleImageWrapperProps}>
                        <LazyImage src={f.image1} altText={f.image1Alt || ""} />
                    </StyledWrapper>
                </Link>
            ) : (
                <StyledWrapper {...singleImageWrapperProps}>
                    <LazyImage src={f.image1} altText={f.image1Alt || ""} />
                </StyledWrapper>
            )
        ) : (
            <StyledWrapper {...styles.imagesArea} className={isTextBetween ? "flex-images-area" : ""}>
                <ImageBlock src={f.image1} alt={f.image1Alt} link={image1Url} />
                {hasImage2 && <ImageBlock src={f.image2} alt={f.image2Alt} link={image2Url} />}
            </StyledWrapper>
        )
    ) : null;

    // Split layout: [image1] [text] [image2]
    if (isSplit) {
        return (
            <StyledWrapper {...styles.outerWrapper} style={{ backgroundColor: bgColor }}>
                <StyledWrapper css={containerCss} className={needsMobileSwap ? "mobile-swap" : ""}>
                    <ImageBlock src={f.image1} alt={f.image1Alt} link={image1Url} />
                    {textAreaBlock}
                    <ImageBlock src={f.image2} alt={f.image2Alt} link={image2Url} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    // Standard 2-column layout: render in desktop DOM order
    return (
        <StyledWrapper {...styles.outerWrapper} style={{ backgroundColor: bgColor }}>
            <StyledWrapper css={containerCss} className={needsMobileSwap ? "mobile-swap" : isTextBetween ? "mobile-text-between" : ""}>
                {desktopImagesFirst ? (
                    <>
                        {imagesAreaBlock}
                        {textAreaBlock}
                    </>
                ) : (
                    <>
                        {textAreaBlock}
                        {imagesAreaBlock}
                    </>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SurFlexibleContent),
    definition: {
        group: "Basic",
        displayName: "Sur Flexible Content",
        fieldDefinitions: [
            {
                name: fields.layout,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                defaultValue: "images-left",
                fieldType: "General",
                tooltip: "Position of images relative to text",
                options: [
                    { displayName: "Images Left, Text Right", value: "images-left" },
                    { displayName: "Text Left, Images Right", value: "images-right" },
                    { displayName: "Image - Text - Image (Split)", value: "split" },
                ],
                sortOrder: 0,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS color value (e.g. 'rgba(242,242,242,0.6)'). Leave empty for transparent.",
                placeholder: "transparent",
                sortOrder: 1,
            },
            {
                name: fields.fullWidth,
                displayName: "Full Width (Edge-to-Edge)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip: "Single image takes 50% edge-to-edge, text takes 50% with its own padding. Padding fields apply to the text area only.",
                sortOrder: 2,
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS color for all text (e.g. '#ffffff' for white on dark backgrounds). Leave empty for default.",
                placeholder: "inherit",
                sortOrder: 3,
            },
            {
                name: fields.ctaColor,
                displayName: "CTA Color",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS color for CTA button text and border. Falls back to Text Color if empty.",
                placeholder: "inherit",
                sortOrder: 4,
            },
            {
                name: fields.preTitle,
                displayName: "Pre-Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tooltip: "Small italic text above the title (e.g. 'Custom Upholstery')",
                placeholder: "Pre-Title",
                sortOrder: 10,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tooltip: "Main heading",
                placeholder: "Title",
                isRequired: true,
                sortOrder: 11,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tooltip: "Description paragraph below the title",
                sortOrder: 12,
            },
            {
                name: fields.ctaLabel,
                displayName: "CTA Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tooltip: "Button text (e.g. 'Explore Customization')",
                placeholder: "CTA Label",
                sortOrder: 13,
            },
            {
                name: fields.ctaLink,
                displayName: "CTA Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.ctaStyle,
                displayName: "CTA Style",
                editorTemplate: "DropDownField",
                defaultValue: "button",
                fieldType: "General",
                tooltip: "Choose whether the CTA is displayed as a button or an underlined hyperlink.",
                options: [
                    { displayName: "Button", value: "button" },
                    { displayName: "Hyperlink", value: "link" },
                ],
                sortOrder: 15,
            },
            {
                name: fields.ctaRequiresAuthentication,
                displayName: "CTA Requires Authentication",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip: "When selected, the CTA is displayed only to authenticated users.",
                sortOrder: 16,
            },
            {
                name: fields.image1,
                displayName: "Image 1",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 20,
            },
            {
                name: fields.image1Alt,
                displayName: "Image 1 Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 21,
            },
            {
                name: fields.image1Link,
                displayName: "Image 1 Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 22,
            },
            {
                name: fields.image2,
                displayName: "Image 2 (Optional)",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Leave empty for single-image layout. Populate for dual-image layout.",
                sortOrder: 23,
            },
            {
                name: fields.image2Alt,
                displayName: "Image 2 Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 24,
            },
            {
                name: fields.image2Link,
                displayName: "Image 2 Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 25,
            },
            {
                name: fields.mobileOrder,
                displayName: "Mobile Order",
                editorTemplate: "DropDownField",
                defaultValue: "text-first",
                fieldType: "General",
                tooltip: "Which content appears first on mobile",
                options: [
                    { displayName: "Text First", value: "text-first" },
                    { displayName: "Images First", value: "images-first" },
                    { displayName: "Text Between Images", value: "text-between" },
                ],
                sortOrder: 30,
            },
            {
                name: fields.textAlign,
                displayName: "Text Alignment (Desktop)",
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                tooltip: "Horizontal alignment of the text block on desktop",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                sortOrder: 31,
            },
            {
                name: fields.mobileTextAlign,
                displayName: "Text Alignment (Mobile)",
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                tooltip: "Horizontal alignment of the text block on mobile",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                sortOrder: 32,
            },
            {
                name: fields.preTitleFontSize,
                displayName: "Pre-Title Font Size (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop pre-title font size (e.g. '24px'). Leave empty for default.",
                placeholder: W.preTitle?.fontSize || "24px",
                sortOrder: 40,
            },
            {
                name: fields.mobilePreTitleFontSize,
                displayName: "Pre-Title Font Size (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile pre-title font size (e.g. '20px'). Leave empty for default.",
                placeholder: W.preTitle?.mobileFontSize || "20px",
                sortOrder: 41,
            },
            {
                name: fields.titleFontSize,
                displayName: "Title Font Size (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop title font size (e.g. '40px'). Leave empty for default.",
                placeholder: W.title?.fontSize || "40px",
                sortOrder: 42,
            },
            {
                name: fields.mobileTitleFontSize,
                displayName: "Title Font Size (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile title font size (e.g. '32px'). Leave empty for default.",
                placeholder: W.title?.mobileFontSize || "32px",
                sortOrder: 43,
            },
            {
                name: fields.paddingVertical,
                displayName: "Padding Top/Bottom (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop vertical padding (e.g. '100px'). Leave empty for default.",
                placeholder: "100px",
                sortOrder: 50,
            },
            {
                name: fields.paddingHorizontal,
                displayName: "Padding Left/Right (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop horizontal padding (e.g. '40px'). Leave empty for default.",
                placeholder: "40px",
                sortOrder: 51,
            },
            {
                name: fields.mobilePaddingVertical,
                displayName: "Padding Top/Bottom (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile vertical padding (e.g. '32px'). Leave empty for default.",
                placeholder: "32px",
                sortOrder: 52,
            },
            {
                name: fields.mobilePaddingHorizontal,
                displayName: "Padding Left/Right (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile horizontal padding (e.g. '16px'). Leave empty for default.",
                placeholder: "16px",
                sortOrder: 53,
            },
            {
                name: fields.imageAspectRatio,
                displayName: "Image Aspect Ratio",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS aspect-ratio for a single image. Use 'auto' for natural proportions. Empty = token default.",
                placeholder: "e.g. auto, 1/1, 16/9",
                sortOrder: 54,
            },
            {
                name: fields.descriptionFontWeight,
                displayName: "Description Font Weight (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop description font-weight (e.g. '300'). Leave empty for default (400).",
                placeholder: W.description?.fontWeight || "400",
                sortOrder: 60,
            },
            {
                name: fields.mobileDescriptionFontWeight,
                displayName: "Description Font Weight (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile description font-weight (e.g. '300'). Leave empty to use desktop value.",
                placeholder: W.description?.fontWeight || "400",
                sortOrder: 61,
            },
            {
                name: fields.preTitleFontWeight,
                displayName: "Pre-Title Font Weight (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop pre-title font-weight (e.g. '300'). Leave empty for default (400).",
                placeholder: W.preTitle?.fontWeight || "400",
                sortOrder: 62,
            },
            {
                name: fields.mobilePreTitleFontWeight,
                displayName: "Pre-Title Font Weight (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile pre-title font-weight (e.g. '300'). Leave empty to use desktop value.",
                placeholder: W.preTitle?.fontWeight || "400",
                sortOrder: 63,
            },
            {
                name: fields.titleFontWeight,
                displayName: "Title Font Weight (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop title font-weight (e.g. '400'). Leave empty for default (500).",
                placeholder: W.title?.fontWeight || "500",
                sortOrder: 64,
            },
            {
                name: fields.mobileTitleFontWeight,
                displayName: "Title Font Weight (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile title font-weight (e.g. '400'). Leave empty to use desktop value.",
                placeholder: W.title?.fontWeight || "500",
                sortOrder: 65,
            },
            {
                name: fields.gap,
                displayName: "Gap",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS gap between image and text areas. Default: 60px.",
                placeholder: "e.g. 60px",
                sortOrder: 55,
            },
            {
                name: fields.textContentMaxWidth,
                displayName: "Text Content Max Width",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Optional CSS max-width value for the text content (e.g. '32rem', '600px', '80%').",
                placeholder: "e.g. 32rem, 600px, 80%",
                sortOrder: 56,
            },
        ],
    },
};

export default widgetModule;
